<template>
  <v-dialog :model-value="open" max-width="820" @update:model-value="(value: boolean) => !value && emit('close')">
    <div v-if="report" class="flex max-h-[88vh] flex-col gap-4 overflow-y-auto rounded-xl bg-white p-5">
      <div class="flex items-start justify-between gap-3">
        <div>
          <h3 class="m-0 text-base font-semibold text-gray-900">Capacity by customer</h3>
          <p class="mb-0 mt-0.5 text-xs text-gray-500">{{ weekLabel }} · {{ vendorCode }}</p>
        </div>
        <button class="text-gray-400 hover:text-gray-600" type="button" aria-label="Close report" @click="emit('close')">✕</button>
      </div>

      <p v-if="report.scheduledTotal === 0" class="m-0 rounded-lg border border-gray-200 bg-gray-50 px-4 py-6 text-center text-sm text-gray-500">
        Nothing is scheduled for this week — {{ report.plannedTotal.toLocaleString() }} of capacity is planned and entirely unused.
      </p>

      <template v-else>
        <div class="flex items-center gap-6 rounded-lg border border-gray-200 bg-gray-50 px-5 py-4">
          <div class="relative h-40 w-40 shrink-0">
            <svg viewBox="0 0 180 180" class="h-40 w-40" role="img" :aria-label="donutAriaLabel">
              <g fill="none" :stroke-width="DONUT_STROKE">
                <circle v-if="fullRingColor" :cx="DONUT_CENTER" :cy="DONUT_CENTER" :r="DONUT_RADIUS" :stroke="fullRingColor" />
                <path v-for="arc in donutArcs" v-else :key="arc.key" :d="arc.d" :stroke="arc.color" />
              </g>
            </svg>
            <div class="absolute inset-0 flex flex-col items-center justify-center">
              <span class="text-lg font-bold text-gray-900">{{ report.scheduledTotal.toLocaleString() }}</span>
              <span class="text-[10px] text-gray-400">scheduled</span>
            </div>
          </div>

          <div class="min-w-0 flex-1">
            <p class="m-0 mb-2 text-[11px] font-bold uppercase tracking-wide text-gray-500">Share of scheduled work</p>
            <div class="space-y-1.5">
              <div v-for="slice in report.slices" :key="slice.key" class="flex items-center gap-2 text-[13px] text-gray-700">
                <span class="inline-block h-2.5 w-2.5 shrink-0 rounded-sm" :style="{ backgroundColor: slice.color }" />
                <span class="flex-1 truncate">{{ slice.label }}</span>
                <span class="tabular-nums text-gray-900">{{ slice.quantity.toLocaleString() }}</span>
                <span class="w-12 text-right font-semibold tabular-nums text-gray-900">{{ formatPercent(slice.percent) }}</span>
              </div>
            </div>
            <div class="mt-2 flex items-center gap-2 border-t border-gray-200 pt-2 text-xs text-gray-500">
              <span class="flex-1">Unused capacity this week</span>
              <span class="tabular-nums">{{ report.unusedTotal.toLocaleString() }}</span>
              <span class="w-12 text-right font-semibold tabular-nums">{{ formatPercent(report.unusedPercent) }}</span>
            </div>
          </div>
        </div>

        <div>
          <p class="m-0 mb-2 text-[11px] font-bold uppercase tracking-wide text-gray-500">Per capacity type</p>
          <div class="space-y-3">
            <div v-for="type in report.types" :key="type.rowId">
              <div class="flex items-center gap-2.5">
                <span class="w-12 shrink-0 truncate text-xs font-semibold text-gray-700">{{ type.typeCode }}</span>
                <div class="flex h-5 flex-1 overflow-hidden rounded border border-gray-300">
                  <div v-for="share in type.shares" :key="share.key" :style="{ width: `${share.percent}%`, backgroundColor: share.color }" />
                  <div class="flex-1 bg-gray-100" />
                </div>
                <span class="w-28 shrink-0 text-right text-[11px] tabular-nums text-gray-500">{{ plannedLabel(type) }}</span>
              </div>
              <p class="mb-0 ml-[3.625rem] mt-1 text-[11px] text-gray-500">
                <template v-for="share in type.shares" :key="share.key">{{ share.label }} {{ formatPercent(share.percent) }} · </template>
                <span class="text-gray-400">unused {{ formatPercent(type.unusedPercent) }}</span>
              </p>
            </div>
          </div>
        </div>

        <p class="m-0 border-t border-gray-200 pt-3 text-[11px] text-gray-400">
          Unattributed covers POs whose customer code has no mapping ({{ report.unattributedBreakdown.unmappedPos.toLocaleString() }}) and capacity
          blocked without a PO ({{ report.unattributedBreakdown.withoutPo.toLocaleString() }}). Percentages under each type are of that type's planned
          capacity; the donut is share of scheduled work only.
        </p>
      </template>
    </div>
  </v-dialog>
</template>

<script setup lang="ts">
  import { computed } from 'vue';

  import type { CapacityReport, CapacityReportTypeRow } from '@/utils/capacityReport';

  const DONUT_RADIUS = 70;
  const DONUT_STROKE = 28;
  const DONUT_CENTER = 90;
  const PERCENT_DECIMALS = 1;
  const FULL_PERCENT = 100;
  const FULL_TURN = 360;
  const HALF_TURN = 180;
  const QUARTER_TURN = 90;

  const props = defineProps<{ open: boolean; report: CapacityReport | null; weekLabel: string; vendorCode: string }>();
  const emit = defineEmits<{ close: [] }>();

  function formatPercent(value: number): string {
    return `${value.toFixed(PERCENT_DECIMALS)}%`;
  }

  function plannedLabel(type: CapacityReportTypeRow): string {
    return `${type.planned.toLocaleString()}${type.unit ? ` ${type.unit}` : ''} planned`;
  }

  // Degrees clockwise from twelve o'clock.
  function pointAt(degrees: number): string {
    const radians = ((degrees - QUARTER_TURN) * Math.PI) / HALF_TURN;
    return `${DONUT_CENTER + DONUT_RADIUS * Math.cos(radians)} ${DONUT_CENTER + DONUT_RADIUS * Math.sin(radians)}`;
  }

  // Open arc paths, deliberately not stroke-dasharray on a <circle>: a circle is a CLOSED
  // path, so a dash landing on its start/end point is rendered with a mitered line join
  // rather than a flat cap. The final slice always ends exactly there, which put a notch
  // in it every time.
  const donutArcs = computed(() => {
    let start = 0;
    return (props.report?.slices ?? []).map((slice) => {
      const end = start + (slice.percent / FULL_PERCENT) * FULL_TURN;
      const largeArc = end - start > HALF_TURN ? 1 : 0;
      const arc = { key: slice.key, color: slice.color, d: `M ${pointAt(start)} A ${DONUT_RADIUS} ${DONUT_RADIUS} 0 ${largeArc} 1 ${pointAt(end)}` };
      start = end;
      return arc;
    });
  });

  // A lone slice spans the full turn, where an arc from 0° to 360° collapses to a point.
  const fullRingColor = computed(() => (props.report?.slices.length === 1 ? props.report.slices[0].color : null));

  const donutAriaLabel = computed(() =>
    (props.report?.slices ?? []).map((slice) => `${slice.label} ${formatPercent(slice.percent)}`).join(', '),
  );
</script>
